import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { sleep } from '../lib/sleep';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private socket: WebSocket;
  private tokenUsed: string;
  private retryCount = 0;

  private _event = new BehaviorSubject<{ name: string; payload: { [key: string]: unknown } }>(undefined);
  public readonly onSocketEvent: Observable<{ name: string; payload: { [key: string]: unknown } }> =
    this._event.asObservable();

  private _connected = new BehaviorSubject<void>(undefined);
  public readonly onSocketConnected: Observable<void> = this._connected.asObservable();

  constructor(
    private state: StateService,
    private config: AppConfigService,
    @Inject(PLATFORM_ID) private platformId: string,
    private loader: DataLoaderService
  ) {}

  public async connect(): Promise<boolean> {
    if (isPlatformServer(this.platformId)) return;
    return this.loader.get('socket', async () => {
      return new Promise<boolean>(resolve => {
        if (this.socket) this.socket.close();
        if (this.state.tokenData) this.tokenUsed = this.state.tokenData.idToken;
        let url = `${this.config.socketEndpoint}`;
        if (this.tokenUsed) url += `/?jwt=${this.tokenUsed}`;
        this.socket = new WebSocket(url);
        this.socket.onmessage = this.onMessage.bind(this);
        this.socket.onopen = () => {
          this.socketOnOpen();
          resolve(true);
        };
        this.socket.onclose = async () => this.socketOnClose();
        this.socket.onerror = error => console.error('Socket error', error);
      });
    });
  }

  private socketOnOpen() {
    this._connected.next();
  }

  private async socketOnClose() {
    delete this.socket;
    const count = this.retryCount * this.retryCount;
    this.retryCount += 1;
    console.log('Socket closed, attempting to reconnect in ' + count + ' seconds');
    await sleep(1000 * count);
    console.log('Attempting to reconnect to socket');
    if (this.socket) {
      console.log('Socket was already connected again');
    } else {
      this.loader.remove('socket');
      await this.connect();
      console.log('Socket reconnected');
    }
  }

  public disconnect() {
    this.socket?.close();
    this.socket = undefined;
    this.tokenUsed = undefined;
  }

  public async sendMessage(method: string, params: { [key: string]: string }) {
    await this.connect();
    if (!this.socket) return console.warn('No socket after connect');
    if (this.socket.readyState === 1) {
      this.socket.send(JSON.stringify({ params, method, jsonrpc: '2.0' }));
    } else {
      console.log('retrying sendMessage in 250ms as socket is not ready');
      await sleep(250);
      this.sendMessage(method, params);
    }
  }

  private onMessage(ev: MessageEvent<string>) {
    this._event.next(JSON.parse(ev.data));
  }
}
